import ArrowSVG from '@public/icons/arrow.svg';
import classNames from 'classnames';
import isEqual from 'lodash.isequal';
import { useRouter } from 'next/router';
import { forwardRef, useEffect, useState } from 'react';
import {
    ApiCommunityEventsRequest,
    AppCommunityEventsResponse,
    useCommunityEventsQuery,
} from '@/shared/api/community-events';
import Image from '@/shared/components/shared/Image';
import Link from '@/shared/components/shared/Link';
import ListPagination from '@/shared/components/shared/ListPagination';
import Loader from '@/shared/components/shared/Loader';
import Responsive from '@/shared/components/shared/Responsive';
import Reveal from '@/shared/components/shared/Reveal';
import { scrollToHref } from '@/shared/components/shared/ScrollToLink/ScrollToLink';
import Typografed from '@/shared/components/shared/Typografed';
import { usePreserveScrollY } from '@/shared/hooks/use-preserve-scroll-y';
import { tp } from '@/shared/lib/typograf';
import { chpu } from '@/shared/lib/utils/filter/chpu';
import { encodeUrl } from '@/shared/lib/utils/filter/chpu/encode-url';
import { convertDates } from './utils/convert-dates';

export interface CommunityEventsSectionProps {
    initialCommunityEventsRequest: ApiCommunityEventsRequest;
    initialCommunityEventsResponse: AppCommunityEventsResponse;
    eventType: Exclude<ApiCommunityEventsRequest['type'], undefined>;
}

export type Props = React.HTMLAttributes<HTMLElement> & CommunityEventsSectionProps;

const CommunityEventsSection = forwardRef<HTMLDivElement, Props>(
    ({ initialCommunityEventsRequest, initialCommunityEventsResponse, eventType, ...props }, ref) => {
        const router = useRouter();
        const [params, setParams] = useState<ApiCommunityEventsRequest>(initialCommunityEventsRequest);

        usePreserveScrollY('/community/');

        const scrollToOrigin = () => scrollToHref('#community-events-list');

        const { data, isFetching, error } = useCommunityEventsQuery({ params }, initialCommunityEventsResponse);

        const onPaginationChange = (page: number) => {
            setParams((prevParams) => {
                const newParams = { ...prevParams };

                if (page === 1) {
                    delete newParams.page;
                } else {
                    newParams.page = page;
                }

                router.push(
                    {
                        pathname: router.pathname,
                        query: { ...router.query, ...newParams },
                    },
                    encodeUrl(router.asPath, newParams, { ignoredKeys: ['limit'] }),
                    { shallow: true, scroll: false },
                );

                requestAnimationFrame(scrollToOrigin);

                return newParams;
            });
        };

        const events = data?.data?.items ?? [];

        useEffect(() => {
            setParams((prevParams) => {
                if (prevParams.type === eventType) {
                    return prevParams;
                }

                const newParams = { ...prevParams, type: eventType };
                delete newParams.page;

                const newParamsWithoutDefaultEventType: Record<string, any> = { ...newParams };

                if (eventType === 'future') {
                    delete newParamsWithoutDefaultEventType.type;
                }

                if (!isEqual(prevParams, newParamsWithoutDefaultEventType)) {
                    router.push(
                        {
                            pathname: router.pathname,
                            query: { ...router.query, ...newParams },
                        },
                        encodeUrl(router.asPath, newParams, { ignoredKeys: ['limit'] }),
                        { shallow: true, scroll: false },
                    );

                    requestAnimationFrame(scrollToOrigin);
                }

                return newParams;
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [eventType]);

        /**
         * Возврат назад по истории
         */
        useEffect(() => {
            const onRouteChange = (nextPagePathname: string) => {
                if (nextPagePathname.replace(/\/filter\/.*/, '') !== router.pathname) {
                    return;
                }

                const paramsFromUrl = chpu.parseUrlToParams(nextPagePathname);

                paramsFromUrl.page =
                    typeof paramsFromUrl.page === 'string' ? parseInt(paramsFromUrl.page) : paramsFromUrl.page;

                if (!Object.is(params, paramsFromUrl)) {
                    setParams((prevParams) => ({ ...prevParams, ...paramsFromUrl }));
                }
            };

            router.events.on('routeChangeStart', onRouteChange);

            return () => {
                router.events.off('routeChangeStart', onRouteChange);
            };
        }, [router.events, params, router.pathname]);

        return (
            <div
                {...props}
                ref={ref}
                id="community-events-list"
                className={classNames('community-events-section wrapper js-header-theme-trigger', props.className)}
                data-theme="dark"
            >
                {error ? <div className="text-l error-message news-list-error-message">{error.message}</div> : null}

                {events.length > 0 ? (
                    <ul className="list-unstyled community-events-list">
                        {events.map((event, i) => {
                            const { days, rest } = convertDates(event.startDate, event.endDate);
                            const isLink = event.hasDetail || event.redirectToMainPage;
                            const Component = isLink ? Link : ('div' as any);

                            return (
                                <Reveal key={i}>
                                    <li className="community-events-list__item">
                                        <Component
                                            href={
                                                isLink
                                                    ? event.redirectToMainPage
                                                        ? '/'
                                                        : `/community/${event.slug}`
                                                    : undefined
                                            }
                                            className="community-event-card"
                                        >
                                            <div className="community-event-card-dates">
                                                <span className="visually-hidden">
                                                    {[...new Set([event.startDate, event.endDate])].join('-')}
                                                </span>
                                                <span className="community-event-card-dates-range" aria-hidden="true">
                                                    <span className="number-small community-event-card-date__lg">
                                                        {days}
                                                    </span>
                                                    <span className="text-s">{rest}</span>
                                                </span>
                                            </div>

                                            <div className="community-event-card-content">
                                                <div className="community-event-card-content__left">
                                                    <Responsive className="community-event-card-image-wrapper">
                                                        {event.img?.src && (
                                                            <Image
                                                                src={event.img.src}
                                                                alt={event.img.alt || event.title || ''}
                                                                width={event.img.width}
                                                                height={event.img.height}
                                                                title={event.img.title}
                                                                className="community-event-card-image responsive__item"
                                                                sizes="50vw"
                                                                priority={event.img.priority}
                                                            />
                                                        )}
                                                    </Responsive>
                                                </div>

                                                <div className="community-event-card-content__right community-event-card-info">
                                                    <div className="community-event-card-info__top">
                                                        <div className="community-event-card-title text-xl">
                                                            {tp(event.title)}
                                                        </div>
                                                        {event.location && (
                                                            <p
                                                                className="community-event-card-location text-m"
                                                                dangerouslySetInnerHTML={{ __html: tp(event.location) }}
                                                            ></p>
                                                        )}
                                                    </div>

                                                    <div className="community-event-card-info__bottom">
                                                        {event.description && (
                                                            <div
                                                                className="community-event-card-description text-m wysiwyg"
                                                                dangerouslySetInnerHTML={{
                                                                    __html: tp(event.description),
                                                                }}
                                                            ></div>
                                                        )}
                                                        {isLink && (
                                                            <span className="community-event-card-btn community-event-card-button">
                                                                <ArrowSVG />
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </Component>
                                    </li>
                                </Reveal>
                            );
                        })}
                    </ul>
                ) : (
                    !isFetching && (
                        <Reveal>
                            <div className="text-l news-list-not-found">
                                <Typografed>Мероприятия не найдены</Typografed>
                            </div>
                        </Reveal>
                    )
                )}

                {(data?.data?.totalCount ?? 0) > (params.limit || 0) && (
                    <Reveal>
                        <div className="community-events__pagination">
                            <ListPagination
                                totalPages={Math.ceil((data?.data?.totalCount ?? 0) / (params.limit || 8))}
                                activePage={Number(params.page ?? 1)}
                                onClickHandler={onPaginationChange}
                                onClickPreventDefault
                            />
                        </div>
                    </Reveal>
                )}

                <Loader isFetching={isFetching} />
            </div>
        );
    },
);

CommunityEventsSection.displayName = 'CommunityEventsSection';

export default CommunityEventsSection;
